import { computed, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Store } from '@ngrx/store';

import { AppState } from '../../shared/store/app-state';
import { injectFromStore } from '../../shared/store/selectors/injectFromStore';
import { LegalDocumentService } from '../services/LegalDocument.service';
import { LegalDocumentsActions } from '../store/actions/legalDocument';
import { getDocumentsToSign } from '../store/selectors/legalDocument';

/**
 * Drives the legal documents signing modal.
 *
 * The documents to sign come from the store (seeded by `signingRequested`) and are
 * signed one at a time: each confirmation advances to the next document, and the last
 * confirmation approves the whole batch on the backend before dispatching
 * `documentsApproved` — which clears the queue and closes the modal.
 */
export function useLegalDocumentSigning() {
    const sanitizer = inject(DomSanitizer);
    const legalDocumentService = inject(LegalDocumentService);
    const store = inject<Store<AppState>>(Store);

    const documents = injectFromStore(getDocumentsToSign);
    const index = signal(0);
    const loading = signal(false);

    const queue = computed(() => documents() ?? []);
    const open = computed(() => index() < queue().length);
    const currentDocument = computed(() => queue()[index()]);
    const currentUrl = computed<SafeResourceUrl | undefined>(() => {
        const document = currentDocument();
        // `#navpanes=0` hides the native PDF viewer pages/thumbnails sidebar.
        return document ? sanitizer.bypassSecurityTrustResourceUrl(`${document.url}#navpanes=0`) : undefined;
    });

    const approveCurrent = (): void => {
        if (loading()) return;

        if (index() < queue().length - 1) {
            index.update((current) => current + 1);
            return;
        }

        loading.set(true);
        legalDocumentService.approveLegalDocuments().subscribe({
            next: () => {
                store.dispatch(LegalDocumentsActions.documentsApproved({ documents: queue() }));
                index.set(0);
                loading.set(false);
            },
            error: () => {
                loading.set(false);
            },
        });
    };

    return {
        open,
        currentDocument,
        currentUrl,
        loading: loading.asReadonly(),
        approveCurrent,
    };
}
